import React, { useState, useCallback, useEffect, useMemo } from "react"
import BreadCrumb from "../breadcrumb";
import InputRadio from "../input-radio";
import NoDataView from "../no-data-view";
import InputSearch from "../input-search";

const FolderNavigation = ({ items, onClick, className, isLoading, withSearch, selected=[], emptyContent, children, breadCrumbs: breadCrumbsValue, onBreadCrumbChange, onBackButtonClick, disableBackButton, noDataText = "No items found" }) => {
  const [loading, setLoading] = useState(false);
  const [breadCrumbs, setBreadCrumbs] = useState([]);
  const [navItems, setNavItems] = useState([]);
  const [searchText, setSearchText] = useState('');

  const updateBreadCrumb = useCallback((newBreadCrumb=[]) => {
    // This will fetch the items to display based on the breadcrumb
    let itemsToReturn = items;
    for(const breadCrumb of newBreadCrumb){
      itemsToReturn = itemsToReturn.find(item => item.id === breadCrumb.id)?.children || [];
    }
    setSearchText(""); // remove search text when breadcrumb changes
    setNavItems(itemsToReturn);
    setBreadCrumbs(newBreadCrumb);
    onBreadCrumbChange && onBreadCrumbChange(newBreadCrumb)
  },[items, onBreadCrumbChange])

  const handleClick = useCallback(async(item)=>{
    try {
      if(!item?.children && onClick){
        setLoading(true)
        await onClick(item)
        setLoading(false)
      }
      if(item.navigable !== false){ // means if we not pass this flag that by default all things will be navigable
        updateBreadCrumb([...breadCrumbs, item])
      }
    } finally {
      setLoading(false)
    }
  },[onClick, breadCrumbs, updateBreadCrumb])

  const filteredItems = useMemo(() => {
    // apply search filter
    return navItems.filter((item) => (item?.displayName || "").toLowerCase().includes(searchText.toLowerCase()));
  }, [navItems, searchText]);

  useEffect(() => {
    setNavItems(items)
    updateBreadCrumb(breadCrumbs)
  }, [items, breadCrumbs, updateBreadCrumb])

  useEffect(() => {
    if(breadCrumbsValue){
      updateBreadCrumb(breadCrumbsValue)
    }
  }, [breadCrumbsValue, updateBreadCrumb])
  
  return (
    <div className={`bimmatch-navigation ${className}`}>
      {!!breadCrumbs.length && <BreadCrumb breadCrumbs={breadCrumbs} onChange={updateBreadCrumb} onGoBack={onBackButtonClick} disableBackButton={disableBackButton} />}
      {children && children}
      {
        (isLoading || loading) ? (
          <div className={`page-loader`}></div>
        ) : (
          <>
            {!navItems.length ? (
              <div className="empty-folder-navigation-container">{emptyContent}</div>
            ) : (
              <>
                {withSearch && (
                  <div className="search-container">
                    <InputSearch searchText={searchText} onChangeSearchText={setSearchText} />
                  </div>
                )}
                {filteredItems.length ? (
                  <NavItems
                    items={filteredItems}
                    onItemClick={handleClick}
                    onItemSelect={handleClick}
                    selected={selected}
                  />
                ) : (
                  <NoDataView 
                    noDataText={noDataText} 
                    size="medium" 
                  />
                )}
              </>
            )}
          </>
        )
      }
    </div>
  )

}

const NavItems = ({ items=[], onItemClick, onItemSelect, selected }) => {
  const isItemSelected = useCallback((id) => selected.includes(id), [selected]);
  return (
    <div className="nav-items-container">
      {items.map((item) => (
        <div 
          className={`nav-item ${isItemSelected(item.id) ? 'selected' : ''}`} 
          key={item.id}
          onClick={() => onItemClick?.(item)}
        >
          {item.selectable ? (
            <InputRadio key={item.id} label={item.displayName} value={item.displayName} checked={isItemSelected(item.id)} onChange={() => onItemSelect?.(item)} />
          ) : (
            <p className="nav-item-label">{item.displayName}</p>
          )}
          {item.navigable && (
            <img 
              src="/images/icon/arrow-right.svg" 
              alt="right" 
              className="right-arrow" 
            />
          )}
        </div>
      ))}
    </div>
  )
}

export default FolderNavigation